import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Table,
  Button,
  Space,
  Avatar,
  message,
  Input,
  Card,
  Popconfirm,
  Select,
  Tooltip,
  Badge,
} from 'antd';
import { PlusOutlined, DeleteOutlined, UserOutlined, ReloadOutlined, AlertOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import StandardPage from '../components/Layout/StandardPage';
import { CustomerDTO, listCustomers, deleteCustomer } from '../services/customers';
import CreateCustomerDrawer from '../components/customers/CreateCustomerDrawer';
import { useNavigate } from 'react-router-dom';
import ManageCustomerAccessDrawer from '../components/users/ManageCustomerAccessDrawer';

import { getLatestViewsByCustomer } from '../services/views';
import CustomerViewsDrawer from '../components/views/CustomerViewsDrawer';
import ViewsReminderDrawer from '../components/views/ViewsReminderDrawer';
import EditCustomerDrawer from '../components/customers/EditCustomerDrawer';

const { Search } = Input;

const Customers: React.FC = () => {
  const [data, setData] = useState<CustomerDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [sectorFilter, setSectorFilter] = useState<number | 'ALL'>('ALL');

  // Gestisci accessi
  const [selectedCustomer, setSelectedCustomer] = useState<CustomerDTO | null>(null);
  const [manageOpen, setManageOpen] = useState(false);
  const [editDrawerOpen, setEditDrawerOpen] = useState(false);

  // Drawer “Inserisci views”
  const [viewsOpen, setViewsOpen] = useState(false);
  const [viewsCustomerId, setViewsCustomerId] = useState<number | null>(null);

  // Promemoria views
  const [remDrawerOpen, setRemDrawerOpen] = useState(false);
  const [reminderLoading, setReminderLoading] = useState(false);
  const [reminderList, setReminderList] = useState<CustomerDTO[]>([]);
  const [reminderCount, setReminderCount] = useState(0);

  const navigate = useNavigate();

  const load = async () => {
    try {
      setLoading(true);
      const items = await listCustomers();
      setData(Array.isArray(items) ? items : []);
    } catch (err: any) {
      message.error(err?.message || 'Errore durante il caricamento clienti');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const sectorOptions = useMemo(() => {
    const map = new Map<number, { value: number; label: string }>();
    for (const c of data) {
      const s = (c as any).sector as { id: number; name: string } | undefined | null;
      if (s && typeof s.id === 'number' && s.name) {
        map.set(s.id, { value: s.id, label: s.name });
      }
    }
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [data]);

  const filteredData = useMemo(() => {
    const searchLower = searchText.toLowerCase().trim();

    return (data || []).filter((customer) => {
      if (sectorFilter !== 'ALL') {
        const s = (customer as any).sector as { id?: number } | undefined | null;
        if (!s || s.id !== sectorFilter) return false;
      }
      if (!searchLower) return true;

      const sectorName = ((customer as any).sector?.name ?? '') as string;

      return (
        (customer.businessName ?? '').toLowerCase().includes(searchLower) ||
        (customer.email ?? '').toLowerCase().includes(searchLower) ||
        (customer.phone ?? '').toLowerCase().includes(searchLower) ||
        (customer.vatNumber ?? '').toLowerCase().includes(searchLower) ||
        (customer.taxCode ?? '').toLowerCase().includes(searchLower) ||
        sectorName.toLowerCase().includes(searchLower)
      );
    });
  }, [data, searchText, sectorFilter]);

  // Calcolo promemoria (clienti con accessi ma senza view negli ultimi 30 giorni)
  const computeViewsReminder = useCallback(async (customers: CustomerDTO[]) => {
    try {
      setReminderLoading(true);
      const withAccess = customers.filter((c: any) => c?.hasUserAccess);
      const sinceLimit = dayjs().subtract(30, 'day');

      const checks = await Promise.allSettled(
        withAccess.map(async (c) => {
          try {
            const latest = await getLatestViewsByCustomer(c.id!);
            const ts = latest?.createdAt ? dayjs(latest.createdAt) : null;
            const needsReminder = !ts || ts.isBefore(sinceLimit, 'day');
            return needsReminder ? c : null;
          } catch {
            // nessuna view o errore => consideralo da ricordare
            return c;
          }
        })
      );

      const list = checks
        .map((res) => (res.status === 'fulfilled' ? res.value : null))
        .filter((c): c is CustomerDTO => !!c);

      setReminderList(list);
      setReminderCount(list.length);
    } finally {
      setReminderLoading(false);
    }
  }, []);

  useEffect(() => {
    if (data.length) {
      computeViewsReminder(data);
    } else {
      setReminderList([]);
      setReminderCount(0);
    }
  }, [data, computeViewsReminder]);

  const columns = useMemo(
    () => [
      {
        title: 'Cliente',
        dataIndex: 'businessName',
        key: 'businessName',
        width: "30%",
        render: (_: any, record: CustomerDTO) => (
          <Space>
            <Avatar
              icon={<UserOutlined />}
              style={{
                backgroundColor: record.isActive ? '#52c41a' : '#ff4d4f',
                color: 'white'
              }}
            />
            <div>
              <div style={{ fontWeight: 500 }}>{record.businessName}</div>
              {record.email && (
                <div style={{ fontSize: 12, opacity: 0.82, color: '#666' }}>
                  {record.email}
                </div>
              )}
            </div>
          </Space>
        ),
      },
      {
        title: 'Note cliente',
        dataIndex: 'notes',
        key: 'notes',
        width: "50%",
        render: (v: string) => v || <span style={{ color: '#ccc' }}>—</span>
      },
      /*{
        title: 'Settore',
        dataIndex: 'sector',
        key: 'sector',
        width: 200,
        render: (_: any, record: CustomerDTO) => {
          const sector = (record as any).sector as { name?: string; icon?: string | null } | null | undefined;
          if (!sector?.name) return <span style={{ color: '#ccc' }}>—</span>;
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>{sector.name}</span>
            </div>
          );
        },
      }, */
      {
        title: 'Azioni',
        key: 'actions',
        width: 320,
        render: (_: any, record: CustomerDTO) => (
          <span onClick={(e) => e.stopPropagation()}>
            <Space wrap>
              <Button
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedCustomer(record);
                  setEditDrawerOpen(true);
                }}
              >
                Modifica info
              </Button>

               <Button
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedCustomer(record);
                  setManageOpen(true);
                }}
              >
                Gestisci accessi
              </Button>

              {/* 👇 NUOVO: Inserisci views */}
              <Button
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  setViewsCustomerId(record.id!);
                  setViewsOpen(true);
                }}
              >
                Inserisci views
              </Button>

              <Popconfirm
                title="Sei sicuro di voler eliminare questo cliente?"
                description="Questa azione non può essere annullata."
                okText="Sì, elimina"
                cancelText="Annulla"
                okButtonProps={{ danger: true }}
                onOpenChange={(_, e) => e?.stopPropagation?.()}
                onCancel={(e) => e?.stopPropagation?.()}
                onConfirm={async (e) => {
                  e?.stopPropagation?.();
                  try {
                    await deleteCustomer(record.id!);
                    message.success('Cliente eliminato');
                    const next = data.filter(c => c.id !== record.id);
                    setData(next);
                    // Ricalcola promemoria
                    computeViewsReminder(next);
                  } catch (err: any) {
                    message.error(err?.message || 'Errore durante eliminazione');
                  }
                }}
              >
                <Button
                  type="primary"
                  icon={<DeleteOutlined />}
                  size="small"
                  danger
                  onClick={(e) => e.stopPropagation()}
                >
                  Elimina
                </Button>
              </Popconfirm>
            </Space>
          </span>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data]
  );

  // Actions top: label dev'essere string (ActionButton)
  const actions = [
    { label: 'Nuovo Cliente', type: 'primary' as const, icon: <PlusOutlined />, onClick: () => setCreateOpen(true) },
    {
      label: 'Promemoria views',
      icon: (
        <Tooltip title="Mostra i clienti che hanno gli accessi ma non hanno inserito una view negli ultimi 30 giorni">
          <Badge count={reminderCount} overflowCount={999} size="small">
            <AlertOutlined />
          </Badge>
        </Tooltip>
      ),
      onClick: async () => {
        await computeViewsReminder(data);
        setRemDrawerOpen(true);
      },
    },
    { label: 'Ricarica', type: 'default' as const, icon: <ReloadOutlined />, onClick: () => load() },
  ];

  const breadcrumbs = [{ title: 'Home', href: '/' }, { title: 'Clienti' }];

  return (
    <>
      <StandardPage
        title="Gestione Clienti"
        subtitle="Visualizza e gestisci tutti i tuoi clienti"
        breadcrumbs={breadcrumbs}
        actions={actions}
      >
        {/* Barra di ricerca + Filtro settore */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 260px', gap: 12, marginBottom: 16 }}>
          <Search
            placeholder="Cerca per nome, email, telefono, P.IVA, codice fiscale o settore..."
            allowClear
            size="large"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            enterButton={false}
          />
          <Select
            size="large"
            value={sectorFilter}
            onChange={(v) => setSectorFilter(v)}
            options={[
              { value: 'ALL', label: 'Tutti i settori' },
              ...sectorOptions
            ]}
          />
        </div>

        {/* Tabella */}
        <Card
          style={{ borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
          bodyStyle={{ padding: 0 }}
        >
          <Table<CustomerDTO>
            rowKey="id"
            columns={columns as any}
            dataSource={Array.isArray(filteredData) ? filteredData : []}
            loading={loading}
            scroll={{ x: 800 }}
            pagination={{
              style: { marginRight: 16 },
              total: filteredData.length,
              defaultPageSize: 8,
              pageSizeOptions: [8, 10, 20, 50, 100],
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} di ${total} clienti`,
            }}
            onRow={(record) => ({
              onClick: () => navigate(`/customers/${record.id}`),
              style: { cursor: 'pointer' },
            })}
          />
        </Card>
      </StandardPage>

      <CreateCustomerDrawer open={createOpen} onClose={() => setCreateOpen(false)} onCreated={load} />

      <ManageCustomerAccessDrawer
        open={manageOpen}
        onClose={() => {
          setManageOpen(false);
          setSelectedCustomer(null);
        }}
        customer={selectedCustomer}
        onSaved={() => {
          load();
          computeViewsReminder(data); // ricalcola dopo eventuale creazione accessi
        }}
      />

      {/* Drawer elenco clienti per “Promemoria views” */}
      <ViewsReminderDrawer
        open={remDrawerOpen}
        onClose={() => setRemDrawerOpen(false)}
        loading={reminderLoading}
        items={reminderList}
        onOpenCustomerViews={(cid) => {
          setViewsCustomerId(cid);
          setViewsOpen(true);
        }}
      />

      <EditCustomerDrawer
        open={editDrawerOpen}
        onClose={() => {
          setEditDrawerOpen(false);
          setSelectedCustomer(null);
        }}
        customer={selectedCustomer}
        onUpdated={(u) => {
          setSelectedCustomer(u);
          void load();
        }}
      />

      {/* Drawer "Inserisci views" ancorato al customer selezionato */}
      <CustomerViewsDrawer
        open={viewsOpen}
        onClose={() => setViewsOpen(false)}
        customerId={viewsCustomerId ?? 0}
        onViewsChanged={() => {
          void computeViewsReminder(data);
        }}
      />
    </>
  );
};

export default Customers;
